import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { BankAPI } from '../api/bancos.api'
import { QueryKeys } from './querykeys'

type CreateInitialBalancePayload = {
  bankId: number
  initialBalance: number
  effectiveDate: string
}

type CreateInitialBalanceRequestPayload =
  | CreateInitialBalancePayload
  | CreateInitialBalancePayload[]

type UpdateInitialBalancePayload = {
  initialBalance: number
}

const bankAPI = new BankAPI()

const normalizeList = (payload: any) => {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  return []
}

export const useQueryBankAccountBanks = (businessId?: number, options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: [QueryKeys.GET_BANK_ACCOUNT_BANKS, businessId],
    enabled: (options?.enabled ?? true) && !!businessId,
    queryFn: async () => {
      const response = await bankAPI.getBanks(Number(businessId))
      return normalizeList(response?.data)
    }
  })

export const useQueryBankAccountInitialBalances = (
  businessId?: number,
  bankId?: number,
  options?: { enabled?: boolean }
) =>
  useQuery({
    queryKey: [QueryKeys.GET_BANK_ACCOUNT_INITIAL_BALANCES, businessId, bankId ?? null],
    enabled: (options?.enabled ?? true) && !!businessId,
    queryFn: async () => {
      const response = await bankAPI.getInitialBalances(Number(businessId), bankId)
      return normalizeList(response?.data)
    }
  })

export const useMutationCreateBankAccountInitialBalance = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      businessId,
      payload
    }: {
      businessId: number
      payload: CreateInitialBalanceRequestPayload
    }) => bankAPI.createInitialBalance(businessId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_BANK_ACCOUNT_INITIAL_BALANCES, variables.businessId]
      })
    }
  })
}

export const useMutationUpdateBankAccountInitialBalance = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      businessId,
      initialBalanceId,
      payload
    }: {
      businessId: number
      initialBalanceId: number
      payload: UpdateInitialBalancePayload
    }) => bankAPI.updateInitialBalance(businessId, initialBalanceId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_BANK_ACCOUNT_INITIAL_BALANCES, variables.businessId]
      })
    }
  })
}

export const useMutationDeleteBankAccountInitialBalance = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      businessId,
      initialBalanceId
    }: {
      businessId: number
      initialBalanceId: number
    }) => bankAPI.deleteInitialBalance(businessId, initialBalanceId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_BANK_ACCOUNT_INITIAL_BALANCES, variables.businessId]
      })
    }
  })
}
